import api from './api';
import { getCompany, buildCompanyHeaderHtml, fmtDateDMY } from './company';

// Rupee formatter used inside the printed HTML. Kept local to avoid a
// dependency cycle with pages that also expose their own `fmt`.
const fmt = (n) => new Intl.NumberFormat('en-IN', {
  style: 'currency', currency: 'INR', maximumFractionDigits: 0,
}).format(n || 0);

/**
 * Render + open the printable invoice preview in a new tab.
 *
 * Single source of truth for the invoice print layout — kept here so the
 * Invoices list "Actions → Print", the Edit page, and the Create/Post-save
 * flow all render an identical bill (company header, Bill-To block,
 * Applicant chip when different, blue-headed items table, grand totals).
 *
 * @param {object}  opts
 * @param {object}  opts.invoice        — invoice-like object: { invoice_number,
 *                                          created_at, customer_id, customer_name,
 *                                          applicant_name, items, discount, total,
 *                                          amount_paid, balance_due, payment_mode }
 * @param {Array}   opts.finAccounts    — finance-accounts list; used to
 *                                          resolve `payment_mode` id → "Name (type)".
 * @param {object=} opts.customer       — optional pre-fetched customer doc; if
 *                                          absent we hit `/api/customers/:id`.
 * @param {string=} opts.title          — window <title>; defaults to invoice #.
 */
export async function printInvoice({ invoice, finAccounts = [], customer, title }) {
  if (!invoice) return;
  const company = await getCompany();
  const headerHtml = buildCompanyHeaderHtml(company);

  // Resolve customer — caller can pass in a hydrated doc to skip the fetch.
  let cust = customer;
  if (!cust && invoice.customer_id) {
    try {
      const res = await api.get(`/customers/${invoice.customer_id}`);
      cust = res.data;
    } catch { /* fall back to whatever's on the invoice */ }
  }
  const custName = cust?.name || invoice.customer_name || '—';
  const custAddress = cust?.address || '';
  const custPhone = cust?.phone || '';

  const applicant = (invoice.applicant_name || '').trim();
  const showApplicant = applicant && applicant.toLowerCase() !== (custName || '').trim().toLowerCase();

  const acc = finAccounts.find(a => a.id === invoice.payment_mode);
  const modeLabel = acc ? `${acc.name} (${acc.type})` : (invoice.payment_mode || '-');

  const itemRows = (invoice.items || []).filter(i => i && (i.service_id || i.service_name)).map((i, idx) => {
    const stripe = idx % 2 === 1 ? 'background:#F9FAFB' : '';
    return `<tr style="${stripe}">
      <td style="padding:8px 10px;font-size:13px;border-bottom:1px solid #E4E4E7">${i.service_name || ''}</td>
      <td style="padding:8px 10px;font-size:13px;text-align:center;border-bottom:1px solid #E4E4E7">${i.quantity ?? 1}</td>
      <td style="padding:8px 10px;font-size:13px;text-align:right;border-bottom:1px solid #E4E4E7">${fmt(i.service_charge)}</td>
      <td style="padding:8px 10px;font-size:13px;text-align:right;border-bottom:1px solid #E4E4E7">${fmt(i.govt_fee)}</td>
      <td style="padding:8px 10px;font-size:13px;text-align:right;border-bottom:1px solid #E4E4E7;font-weight:600">${fmt(i.total)}</td>
    </tr>`;
  }).join('');

  const invNo = invoice.invoice_number || '(auto on save)';
  const dateLabel = fmtDateDMY(invoice.created_at || invoice.invoice_date || new Date().toISOString());
  const winTitle = title || invNo || 'Invoice Preview';

  const win = window.open('', '_blank');
  if (!win) return; // popup blocked
  win.document.write(`<html><head><title>${winTitle}</title><style>
    body { font-family: 'Helvetica Neue', Arial, sans-serif; padding: 32px; color: #0A0A0A; max-width: 760px; margin: auto; }
    table { width: 100%; border-collapse: collapse; }
    .label { font-size:10px;text-transform:uppercase;letter-spacing:0.08em;color:#71717A;font-weight:700;margin-bottom:2px }
    .total-row { display:flex;justify-content:space-between;align-items:center;padding:6px 0;font-size:14px }
    .total-row.grand { border-top:2px solid #0A0A0A;padding-top:10px;margin-top:6px;font-size:17px;font-weight:bold }
    .total-row.paid { color:#059669 }
    .total-row.due { color:#DC2626;font-weight:600 }
  </style></head><body>
    ${headerHtml}

    <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:20px;gap:24px">
      <div style="flex:1">
        <div class="label">Bill To</div>
        <div style="font-size:15px;font-weight:bold;color:#0A0A0A;margin-top:4px">${custName}</div>
        ${showApplicant ? `<div style="font-size:12px;color:#7C3AED;margin-top:3px"><em>Applicant:</em> ${applicant}</div>` : ''}
        ${custAddress ? `<div style="font-size:12px;color:#52525B;margin-top:3px;white-space:pre-line">${custAddress}</div>` : ''}
        ${custPhone ? `<div style="font-size:12px;color:#52525B;margin-top:2px">Ph: ${custPhone}</div>` : ''}
      </div>
      <div style="text-align:right;flex-shrink:0">
        <div style="font-size:16px;font-weight:bold;color:#3B82F6;font-family:monospace">${invNo}</div>
        <div style="font-size:13px;color:#0A0A0A;font-weight:600;margin-top:4px">${dateLabel}</div>
      </div>
    </div>

    <table>
      <thead>
        <tr style="background:#3B82F6;color:#fff">
          <th style="padding:10px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:0.08em">Service</th>
          <th style="padding:10px;text-align:center;font-size:11px;text-transform:uppercase;letter-spacing:0.08em">Qty</th>
          <th style="padding:10px;text-align:right;font-size:11px;text-transform:uppercase;letter-spacing:0.08em">Svc Charge</th>
          <th style="padding:10px;text-align:right;font-size:11px;text-transform:uppercase;letter-spacing:0.08em">Govt Fee</th>
          <th style="padding:10px;text-align:right;font-size:11px;text-transform:uppercase;letter-spacing:0.08em">Total</th>
        </tr>
      </thead>
      <tbody>${itemRows}</tbody>
    </table>

    <div style="margin-top:20px;max-width:340px;margin-left:auto">
      ${(invoice.discount || 0) > 0 ? `<div class="total-row" style="color:#F59E0B"><span>Discount</span><span>−${fmt(invoice.discount)}</span></div>` : ''}
      <div class="total-row grand"><span>Grand Total</span><span>${fmt(invoice.total)}</span></div>
      <div class="total-row paid"><span>Amount Paid</span><span>${fmt(invoice.amount_paid || 0)}</span></div>
      <div class="total-row due"><span>Amount Due</span><span>${fmt(Math.max(0, invoice.balance_due || 0))}</span></div>
      <div class="total-row" style="font-size:12px;color:#52525B;border-top:1px dashed #E4E4E7;padding-top:8px"><span>Payment Mode</span><span style="font-weight:600;color:#0A0A0A">${modeLabel}</span></div>
    </div>

    <div style="margin-top:32px;text-align:center;font-size:10px;color:#A1A1AA;border-top:1px solid #E4E4E7;padding-top:10px">
      Thank you for your business!
    </div>
  </body></html>`);
  win.document.close();
  win.print();
}
